type SSECallbacks = {
  onMessage: (data: Record<string, unknown>) => void;
  onReconnect: (attempt: number) => void;
  /** Reabriu depois de já ter estado conectado (queda de rede, aba oculta): busque o que perdeu. */
  onReconnected: () => void;
  onMaxRetriesReached: () => void;
};

export class SSEManager {
  private url: string;
  private callbacks: SSECallbacks;
  private es: EventSource | null = null;
  private retryCount = 0;
  private retryTimer: number | null = null;
  private maxRetries = 6;
  private retryDelays = [1000, 2000, 4000, 8000, 16000, 30000];
  private destroyed = false;
  private hasOpened = false;

  constructor(url: string, callbacks: SSECallbacks) {
    this.url = url;
    this.callbacks = callbacks;
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /** Token renovado: as próximas (re)conexões usam a URL nova. */
  setUrl(url: string): void {
    this.url = url;
  }

  connect(): void {
    if (this.destroyed || this.es) return;

    try {
      this.es = new EventSource(this.url);

      this.es.onmessage = (event: MessageEvent) => {
        this.retryCount = 0; // Reset on successful message
        try {
          const data = JSON.parse(event.data) as Record<string, unknown>;
          this.callbacks.onMessage(data);
        } catch {
          // Ignore malformed JSON
        }
      };

      this.es.onopen = () => {
        this.retryCount = 0;
        if (this.hasOpened) {
          this.callbacks.onReconnected();
        }
        this.hasOpened = true;
      };

      this.es.onerror = () => {
        if (this.es) {
          this.es.close();
          this.es = null;
        }
        if (!this.destroyed) {
          this.scheduleReconnect();
        }
      };
    } catch {
      if (!this.destroyed) {
        this.scheduleReconnect();
      }
    }
  }

  disconnect(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.es) {
      this.es.close();
      this.es = null;
    }
  }

  reconnect(): void {
    this.disconnect();
    this.retryCount = 0;
    if (!this.destroyed) {
      this.connect();
    }
  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      this.disconnect();
    } else if (!this.es && !this.destroyed) {
      this.connect();
    }
  }

  private scheduleReconnect(): void {
    if (this.destroyed) return;

    if (this.retryCount >= this.maxRetries) {
      this.callbacks.onMaxRetriesReached();
      return;
    }

    const delay = this.retryDelays[Math.min(this.retryCount, this.retryDelays.length - 1)] ?? 30000;
    this.retryCount++;
    this.callbacks.onReconnect(this.retryCount);

    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      if (!this.destroyed) {
        this.connect();
      }
    }, delay);
  }

  destroy(): void {
    this.destroyed = true;
    this.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}
